import React, {
  useRef, useState, ReactNode, useEffect, useCallback, useMemo, Children,
} from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

type Props = {
  children: ReactNode;
  slidesToShow?: number;
  totalItems: number;
  NextArrow: React.ComponentType<{ onClick: () => void; color: string }>;
  PrevArrow: React.ComponentType<{ onClick: () => void; color: string }>;
  className?: string;
  onPageChange?: (currentPage: number, totalPages: number) => void;
};

const SliderWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;

  .slider-content {
    flex: 1;
    overflow: hidden;
    margin: 0 8px;
  }

  .slick-slider {
    width: 100%;
  }

  .slick-list {
    overflow: hidden;
    margin: 0;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    height: auto;
    padding: 0 6px;
    > div {
      height: 100%;
    }
  }

  .slider-arrow {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.disabled {
      pointer-events: none;
      opacity: 0.6;
    }
  }

  .empty-slide-placeholder {
    visibility: hidden;
  }
`;

const CustomSlider = function CustomSlider(props: Props) {
  const {
    children,
    slidesToShow = 4,
    totalItems,
    NextArrow,
    PrevArrow,
    className = '',
    onPageChange,
  } = props;

  const sliderRef = useRef<Slider>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = slidesToShow;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  // Calculate how many empty placeholders we need to fill the last page
  const childrenWithPlaceholders = useMemo(() => {
    const childArray = Children.toArray(children);
    const remainder = totalItems % itemsPerPage;

    if (remainder === 0 || totalItems === 0) {
      return childArray;
    }

    const emptySlots = itemsPerPage - remainder;
    const placeholders = Array.from({ length: emptySlots }, (_, index) => (
      <div key={`empty-placeholder-${index}`} className="empty-slide-placeholder">
        <div style={{ height: '100%' }} />
      </div>
    ));

    return [...childArray, ...placeholders];
  }, [children, totalItems, itemsPerPage]);

  // Reset to page 1 if totalItems changes and current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
      sliderRef.current?.slickGoTo(0);
      if (onPageChange) {
        onPageChange(1, totalPages);
      }
    }
  }, [totalItems, totalPages, currentPage, onPageChange]);

  // Notify parent of initial page on mount
  useEffect(() => {
    if (onPageChange) {
      onPageChange(1, totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: itemsPerPage,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    draggable: true,
    touchMove: true,
    accessibility: false,
    swipeToSlide: true,
  };

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage >= totalPages;
  const hasMultiplePages = totalPages > 1;

  const handleNext = useCallback(() => {
    if (!isLastPage && hasMultiplePages) {
      const nextPage = currentPage + 1;
      const targetSlide = (nextPage - 1) * itemsPerPage;
      sliderRef.current?.slickGoTo(targetSlide);
      setCurrentPage(nextPage);
      if (onPageChange) {
        onPageChange(nextPage, totalPages);
      }
    }
  }, [currentPage, isLastPage, hasMultiplePages, itemsPerPage, onPageChange, totalPages]);

  const handlePrev = useCallback(() => {
    if (!isFirstPage && hasMultiplePages) {
      const prevPage = currentPage - 1;
      const targetSlide = (prevPage - 1) * itemsPerPage;
      sliderRef.current?.slickGoTo(targetSlide);
      setCurrentPage(prevPage);
      if (onPageChange) {
        onPageChange(prevPage, totalPages);
      }
    }
  }, [currentPage, isFirstPage, hasMultiplePages, itemsPerPage, onPageChange, totalPages]);

  // Don't render slider if no items
  if (totalItems === 0) {
    return null;
  }

  return (
    <SliderWrapper className={`custom-slider-wrapper ${className}`}>
      <div className={`slider-arrow ${isFirstPage || !hasMultiplePages ? 'disabled' : ''}`}>
        <PrevArrow
          onClick={handlePrev}
          color='#CBCBCA'
        />
      </div>
      <div className="slider-content">
        <Slider ref={sliderRef} {...settings}>
          {childrenWithPlaceholders}
        </Slider>
      </div>
      <div className={`slider-arrow ${isLastPage || !hasMultiplePages ? 'disabled' : ''}`}>
        <NextArrow
          onClick={handleNext}
          color='#CBCBCA'
        />
      </div>
    </SliderWrapper>
  );
};

export default CustomSlider;
